import React from 'react';
import { useLanguage } from '@contexts/LanguageContext';
import { Card, StatusBadge } from '@components/common';
import { formatDate } from '@utils/dateUtils';
import Icon from '@components/Icon/Icon';
import { faCalendar, faUser } from '@fortawesome/free-solid-svg-icons';
import './BorrowingCard.scss';

const BorrowingCard = ({
    borrowing,
    displayType = 'grid',
    displayOptions = {},
    onClick,
    actions
}) => {
    const { t, currentLanguage } = useLanguage();

    // Helper function to format dates simply
    const formatDateSimple = (dateString, includeDayName = false) => {
        return formatDate(dateString, false, currentLanguage, t, false, includeDayName);
    };

    // Helper function to format dynamic field values
    const formatDynamicFieldValue = (value, type) => {
        if (value === null || value === undefined) return '-';

        switch (type) {
            case 'boolean':
                return value ? t('borrowingFields.yes') || 'Yes' : t('borrowingFields.no') || 'No';
            case 'integer':
            case 'float':
                return String(value);
            case 'string':
            case 'select':
            default:
                return value;
        }
    };

    // Get the description field to display
    const getDescriptionField = () => {
        if (!borrowing.dynamic_fields) {
            return null;
        }

        // Find the first field marked as description_field
        for (const [fieldKey, fieldData] of Object.entries(borrowing.dynamic_fields)) {
            // Extract value and definition from the enriched structure
            let fieldValue = fieldData?.value !== undefined ? fieldData.value : fieldData;
            const fieldDef = fieldData?.definition;

            // Guard against double-enriched values (value is itself {value, definition})
            if (fieldValue && typeof fieldValue === 'object' && 'value' in fieldValue) {
                fieldValue = fieldValue.value;
            }

            if (fieldDef?.description_field && fieldValue !== undefined) {
                return {
                    label: fieldDef.label || fieldKey,
                    value: fieldValue,
                    type: fieldDef.type || 'string'
                };
            }
        }

        return null;
    };

    const handleClick = () => {
        if (onClick) {
            onClick(borrowing);
        }
    };

    // Determine if this is a reservation (no borrowing_at) or an active borrowing
    const isReservation = !borrowing.borrowed_at;
    const isActiveBorrowing = !!borrowing.borrowed_at;
    const isPendingValidation = borrowing.validation_status === 'pending';
    const isValidationRejected = borrowing.validation_status === 'rejected';

    // Get the most relevant date range to display
    const getDateDisplay = () => {
        if (borrowing.reserved_from && borrowing.reserved_until) {
            return `${formatDateSimple(borrowing.reserved_from, true)} - ${formatDateSimple(borrowing.reserved_until, true)}`;
        }
        if (borrowing.borrowed_at && borrowing.returned_at) {
            return `${formatDateSimple(borrowing.borrowed_at, true)} - ${formatDateSimple(borrowing.returned_at, true)}`;
        }
        if (borrowing.borrowed_at) {
            return `${t('borrowingView.since') || 'Since'}: ${formatDateSimple(borrowing.borrowed_at, true)}`;
        }
        return null;
    };

    return (
        <div
            className={`borrowing-card borrowing-card--${displayType} ${isReservation ? 'borrowing-card--reservation' : ''} ${isActiveBorrowing ? 'borrowing-card--active-borrowing' : ''} ${isPendingValidation ? 'borrowing-card--pending-validation' : ''} ${isValidationRejected ? 'borrowing-card--validation-rejected' : ''} ${onClick ? 'clickable' : ''}`}
            onClick={handleClick}
        >
            <div className="borrowing-card__header">
                <span className="borrowing-card__type">
                    {isReservation ? t('borrowingView.reservation') : t('borrowingView.borrowing')}
                </span>
                {isPendingValidation && (
                    <span className="borrowing-card__validation-badge borrowing-card__validation-badge--pending">
                        {t('borrowingView.status.pendingValidation')}
                    </span>
                )}
                {isValidationRejected && (
                    <span className="borrowing-card__validation-badge borrowing-card__validation-badge--rejected">
                        {t('borrowingView.status.validationRejected')}
                    </span>
                )}
                <h3 className="borrowing-card__resource-name">
                    {borrowing.resource?.name}
                </h3>
                {getDescriptionField() && (
                    <div className="borrowing-card__description">
                        {formatDynamicFieldValue(getDescriptionField().value, getDescriptionField().type)}
                    </div>
                )}
            </div>

            <div className="borrowing-card__content">
                <div className="borrowing-card__meta">
                    {!displayOptions?.hideUser && (
                        <div className="borrowing-card__user">
                            <Icon icon={faUser} className="borrowing-card__user-icon" />
                            <span className="borrowing-card__user-name">
                                {borrowing.user?.name || borrowing.user?.email || t('borrowing.unknown_user')}
                            </span>
                        </div>
                    )}


                    {getDateDisplay() && (
                        <div className="borrowing-card__dates">
                            <Icon icon={faCalendar} className="borrowing-card__date-icon" />
                            <span className="borrowing-card__date-text">{getDateDisplay()}</span>
                        </div>
                    )}
                </div>

                <div className="borrowing-card__status">
                    <StatusBadge model={borrowing} type="borrowing" className="size-small" />
                </div>
            </div>

            {actions && (
                <div className="borrowing-card__actions">
                    {actions}
                </div>
            )}
        </div>
    );
};

export default BorrowingCard;
